/**
 * Full-viewport Mapbox map for the professional HTML report iframe.
 * Reuses EnhancedMapVisualizationGL — same stack as the base Results page.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import apiService from '@signals/app-shell/services/api';
import { useAuthStore } from '@signals/app-shell/stores';
import EnhancedMapVisualizationGL from '../components/EnhancedMapVisualizationGL';

const ReportMapEmbedPage: React.FC = () => {
  const { jobId: jobIdParam } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authLoading = useAuthStore((s) => s.loading);

  const jobId = Number(jobIdParam);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapData, setMapData] = useState<{
    dwellLocations: any[];
    routes: any[];
    summaryStats: Record<string, any>;
  } | null>(null);

  useEffect(() => {
    const token = searchParams.get('access_token');
    if (token) {
      localStorage.setItem('access_token', token);
      void checkAuth();
    }
  }, [searchParams, checkAuth]);

  useEffect(() => {
    if (!Number.isFinite(jobId) || jobId <= 0) {
      setError('Invalid job id');
      setLoading(false);
      return;
    }

    if (authLoading) {
      return;
    }

    if (!isAuthenticated && !localStorage.getItem('access_token')) {
      setError('Authentication required to load the analysis map.');
      setLoading(false);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        setLoading(true);
        setError(null);

        const jobData = await apiService.getAnalysisJob(jobId);
        if (jobData.status !== 'completed') {
          throw new Error('Analysis is not completed yet.');
        }

        const resultsData = await apiService.getAnalysisResults(jobId);
        if (cancelled) {
          return;
        }

        setMapData({
          dwellLocations: resultsData.dwell_locations || [],
          routes: resultsData.routes || [],
          summaryStats: resultsData.summary_stats || jobData.results_summary || {},
        });
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Failed to load map data');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobId, isAuthenticated, authLoading]);

  const layers = useMemo(() => {
    if (!mapData) {
      return null;
    }
    const summary = mapData.summaryStats || {};
    return {
      heatmapData: summary.heatmap_data || [],
      daytimeHeatmapData: summary.daytime_heatmap_data || [],
      nighttimeHeatmapData: summary.nighttime_heatmap_data || [],
      deviceTracks: summary.device_tracks || {},
      temporalBreakdown: summary.temporal_breakdown,
    };
  }, [mapData]);

  if (loading || authLoading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !mapData || !layers) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          <Typography variant="subtitle2">Map could not be loaded</Typography>
          {error || 'No map data available for this job.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        minHeight: 700,
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <EnhancedMapVisualizationGL
        dwellLocations={mapData.dwellLocations}
        routes={mapData.routes}
        heatmapData={layers.heatmapData}
        daytimeHeatmapData={layers.daytimeHeatmapData}
        nighttimeHeatmapData={layers.nighttimeHeatmapData}
        deviceTracks={layers.deviceTracks}
        temporalBreakdown={layers.temporalBreakdown}
        activeTab={0}
        mapHeight="100%"
      />
    </Box>
  );
};

export default ReportMapEmbedPage;
